import { z } from 'zod'
import { DEFAULT_LOCALE, interpolate, type Locale, type Localized } from './i18n.ts'
import { LABELS, type LabelSets } from './labels.ts'
import { DEFAULT_CURRENCY, formatMoney } from './money.ts'
import { formatQuantityWithUnit, unitSymbol } from './units.ts'

/**
 * In-app notifications, and Web Push for when the app is closed.
 *
 * There is no SMS or email path (D2), so this is the only way the platform reaches
 * someone who is not looking at it. That raises the bar for what earns a
 * notification: every type below corresponds to somebody waiting on somebody else.
 */

export const NOTIFICATION_TYPES = [
  'rfq.created',
  'offer.received',
  'offer.countered',
  'offer.expiring_soon',
  'deal.locked',
  'order.status_changed',
  'sourcing.invited',
  'sourcing.quote_received',
  'po.sent',
  'po.confirmed',
  'po.declined',
  'po.updated',
  'message.received',
  'stock.low',
  'product_proposal.pending',
  'product_proposal.reviewed',
  'account.created',
] as const
export type NotificationType = (typeof NOTIFICATION_TYPES)[number]
export const notificationTypeSchema = z.enum(NOTIFICATION_TYPES)

// ---------------------------------------------------------------------------
// Wording
//
// A notification is stored as a template key plus its values, never as a finished
// sentence. The reason is that the two readers of a row disagree about language:
// push is rendered once, at send time, in the recipient's preference, while the
// notification centre is rendered every time it is opened — possibly after that
// person has switched languages. Storing prose would freeze the first answer and
// make the second one wrong.
// ---------------------------------------------------------------------------

export const NOTIFICATION_TEMPLATE_KEYS = [
  'rfq.created',
  'offer.received',
  'offer.countered',
  'offer.rejected',
  'deal.locked.buyer',
  'deal.locked.desk',
  'order.status_changed',
  'po.sent',
  'po.moved',
  'sourcing.invited',
  'sourcing.quote_received',
  'stock.low',
  'proposal.pending',
  'proposal.approved',
  'proposal.merged',
  'proposal.rejected',
  'message.received',
  'message.received.attachment',
  'message.note',
  'message.note.attachment',
] as const
export type NotificationTemplateKey = (typeof NOTIFICATION_TEMPLATE_KEYS)[number]
export const notificationTemplateKeySchema = z.enum(NOTIFICATION_TEMPLATE_KEYS)

/**
 * A value a template can carry.
 *
 * Money, quantities and enum labels arrive as data rather than as text, because
 * every one of them renders differently per language — "Rs 1,200" against
 * "रु 1,200", "200 bag" against "200 बोरा". Pre-formatting them on the server would
 * bake in whichever language happened to be handy at write time.
 */
export const notificationParamSchema = z.union([
  z.string(),
  z.number(),
  z.object({ money: z.number().int() }),
  z.object({ qty: z.number().int(), unit: z.string() }),
  z.object({ unit: z.string() }),
  /** A key from one of the LABELS sets — `{ set: 'orderStatus', key: 'dispatched' }`. */
  z.object({ set: z.string(), key: z.string() }),
])
export type NotificationParam = z.infer<typeof notificationParamSchema>

export const notificationParamsSchema = z.record(z.string(), notificationParamSchema)
export type NotificationParams = z.infer<typeof notificationParamsSchema>

export const notificationSchema = z.object({
  id: z.string(),
  type: notificationTypeSchema,
  templateKey: notificationTemplateKeySchema.nullable(),
  params: notificationParamsSchema,
  /**
   * Only ever set on rows written before wording moved to templates. New rows
   * carry a `templateKey` instead; the client falls back to these when it is null.
   */
  title: z.string().nullable(),
  body: z.string().nullable(),
  /** Where tapping it lands. A first-party path, resolved by the client router. */
  linkPath: z.string().nullable(),
  readAt: z.number().int().nullable(),
  createdAt: z.number().int(),
})
export type Notification = z.infer<typeof notificationSchema>

export const notificationCountSchema = z.object({
  unread: z.number().int(),
})
export type NotificationCount = z.infer<typeof notificationCountSchema>

/**
 * A browser push subscription, exactly as `PushManager.subscribe()` produces it.
 * `p256dh` and `auth` are the client's public key and shared secret; the server
 * needs both to encrypt a payload it can never read back (RFC 8291).
 */
export const pushSubscriptionSchema = z.object({
  endpoint: z.string().min(1).max(600),
  keys: z.object({
    p256dh: z.string().min(1).max(200),
    auth: z.string().min(1).max(100),
  }),
})
export type PushSubscriptionInput = z.infer<typeof pushSubscriptionSchema>

export const pushStatusSchema = z.object({
  /** Null when the deployment has no VAPID keys, which disables the whole feature. */
  publicKey: z.string().nullable(),
  subscribed: z.boolean(),
})
export type PushStatus = z.infer<typeof pushStatusSchema>

/** Poll cadence for the bell. Cheap enough to leave running on every screen. */
export const NOTIFICATION_POLL_MS = 60_000

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

interface NotificationTemplate {
  title: string
  body?: string
  /** Used instead of `body` when the plural-driving param is exactly 1. */
  bodyOne?: string
  /** Which param decides singular from plural. Defaults to `count`. */
  pluralOn?: string
}

type TemplateSet = Record<NotificationTemplateKey, NotificationTemplate>

const TEMPLATES: Localized<TemplateSet> = {
  en: {
    'rfq.created': {
      title: 'New price request',
      body: '{code} — {count} items',
      bodyOne: '{code} — 1 item',
    },
    'offer.received': {
      title: 'Your price for {code}',
      body: '{total} — valid for {hours} hours',
      bodyOne: '{total} — valid for 1 hour',
      pluralOn: 'hours',
    },
    'offer.countered': { title: 'Counter-offer on {code}', body: '{total} suggested' },
    'offer.rejected': { title: '{code} was turned down', body: '{note}' },
    'deal.locked.buyer': {
      title: 'Order {code} confirmed',
      body: '{total} — we will keep you posted.',
    },
    'deal.locked.desk': { title: 'Order {code} confirmed', body: '{total} locked in.' },
    'order.status_changed': { title: '{code}: {status}', body: '{note}' },
    'po.sent': {
      title: 'New order — {code}',
      body: '{count} items, {total}',
      bodyOne: '1 item, {total}',
    },
    'po.moved': { title: '{code}: {status}', body: '{shop} — {total}' },
    'sourcing.invited': { title: 'Price wanted', body: '{qty} of {product}' },
    'sourcing.quote_received': { title: 'Price in for {code}', body: '{price} per {unit}' },
    'stock.low': { title: 'Running low', body: '{product} — {qty} left' },
    'proposal.pending': { title: 'New item suggested', body: '{name} — waiting for review' },
    'proposal.approved': {
      title: '"{name}" is ready',
      body: 'It is in the catalog — add your price.',
    },
    'proposal.merged': {
      title: '"{name}" is ready',
      body: 'We matched it to "{product}". You can list it now.',
    },
    'proposal.rejected': { title: '"{name}" was not added', body: '{note}' },
    'message.received': { title: '{subject}', body: '{preview}' },
    'message.received.attachment': { title: '{subject}', body: 'Sent an attachment' },
    'message.note': { title: 'Note on "{subject}"', body: '{preview}' },
    'message.note.attachment': { title: 'Note on "{subject}"', body: 'Sent an attachment' },
  },
  ne: {
    'rfq.created': {
      title: 'नयाँ रेट अनुरोध',
      body: '{code} — {count} वटा सामान',
      bodyOne: '{code} — 1 वटा सामान',
    },
    'offer.received': {
      title: '{code} को रेट आयो',
      body: '{total} — {hours} घण्टासम्म मान्य',
      bodyOne: '{total} — 1 घण्टासम्म मान्य',
      pluralOn: 'hours',
    },
    'offer.countered': { title: '{code} मा अर्को रेट आयो', body: '{total} सुझाइयो' },
    'offer.rejected': { title: '{code} अस्वीकार भयो', body: '{note}' },
    'deal.locked.buyer': {
      title: 'अर्डर {code} पक्का भयो',
      body: '{total} — हामी खबर गर्दै जान्छौं।',
    },
    'deal.locked.desk': { title: 'अर्डर {code} पक्का भयो', body: '{total} पक्का भयो।' },
    'order.status_changed': { title: '{code}: {status}', body: '{note}' },
    'po.sent': {
      title: 'नयाँ अर्डर — {code}',
      body: '{count} वटा सामान, {total}',
      bodyOne: '1 वटा सामान, {total}',
    },
    'po.moved': { title: '{code}: {status}', body: '{shop} — {total}' },
    'sourcing.invited': { title: 'रेट चाहियो', body: '{product} — {qty}' },
    'sourcing.quote_received': { title: '{code} को रेट आयो', body: 'प्रति {unit} {price}' },
    'stock.low': { title: 'स्टक सकिँदै', body: '{product} — {qty} बाँकी' },
    'proposal.pending': { title: 'नयाँ सामान सुझाइयो', body: '{name} — हेर्न बाँकी' },
    'proposal.approved': {
      title: '"{name}" तयार भयो',
      body: 'लिस्टमा आइसक्यो — आफ्नो रेट हाल्नुहोस्।',
    },
    'proposal.merged': {
      title: '"{name}" तयार भयो',
      body: 'यसलाई "{product}" सँग मिलाइयो। अब रेट हाल्न सक्नुहुन्छ।',
    },
    'proposal.rejected': { title: '"{name}" थपिएन', body: '{note}' },
    'message.received': { title: '{subject}', body: '{preview}' },
    'message.received.attachment': { title: '{subject}', body: 'फाइल पठाइयो' },
    'message.note': { title: '"{subject}" मा नोट', body: '{preview}' },
    'message.note.attachment': { title: '"{subject}" मा नोट', body: 'फाइल पठाइयो' },
  },
}

function formatParam(value: NotificationParam, locale: Locale): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number') return value.toLocaleString('en-IN')
  if ('money' in value) return formatMoney(value.money, DEFAULT_CURRENCY, { locale })
  if ('qty' in value) return formatQuantityWithUnit(value.qty, value.unit, locale)
  if ('unit' in value) return unitSymbol(value.unit, locale)
  const set = LABELS[locale][value.set as keyof LabelSets] as Record<string, string> | undefined
  return set?.[value.key] ?? value.key
}

/**
 * Turn a stored row into the two lines somebody reads.
 *
 * A body with a placeholder nothing filled is dropped rather than shown with a
 * `{note}` in it. That is what makes the optional halves work — an order moved
 * along without a note, or a shop's own copy of a purchase order that must not
 * name the price — without a second template key for each.
 */
export function renderNotification(
  key: NotificationTemplateKey,
  params: NotificationParams,
  locale: Locale = DEFAULT_LOCALE,
): { title: string; body: string | null } {
  const template = TEMPLATES[locale][key]
  const filled: Record<string, string | number> = {}
  for (const [name, value] of Object.entries(params)) filled[name] = formatParam(value, locale)

  const countParam = params[template.pluralOn ?? 'count']
  const isOne = typeof countParam === 'number' && countParam === 1
  const bodyTemplate = (isOne ? template.bodyOne : undefined) ?? template.body

  const body = bodyTemplate ? interpolate(bodyTemplate, filled) : null
  return {
    title: interpolate(template.title, filled),
    // An unfilled placeholder means the caller had nothing to say there.
    body: body && !/\{\w+\}/.test(body) ? body : null,
  }
}

/** What the reader sees, preferring the template and falling back to a legacy row. */
export function notificationText(
  notification: Pick<Notification, 'templateKey' | 'params' | 'title' | 'body'>,
  locale: Locale = DEFAULT_LOCALE,
): { title: string; body: string | null } {
  if (notification.templateKey) {
    return renderNotification(notification.templateKey, notification.params, locale)
  }
  return { title: notification.title ?? '', body: notification.body }
}
